import React, {useState} from 'react';
import {Context} from '../../context';
import HeadingAndExpandableBody from '../UI/HeadingAndExpandableBody';
import LabelAndInput from '../UI/LabelAndInput';
import Button from '../UI/Button';
import ErrorNote from '../UI/ErrorNote';
import {
  getPasswordErrors,
  getInputValueByName,
  shouldShowField
} from '../../helperFunctions';
import AuthComPromInterface from '../UI/AuthComProm';
import {registrationFields as regFields} from '../../settings.js';

function MyAccount(props) {
  const {
    localeText,
    updateAccount,
    updateAddress,
    account,
    setSnackBarMessage,
    loading
  } = React.useContext(Context);

  const [errors, setErrors] = useState({});

  const {emailOrMail, AuthComProm} = AuthComPromInterface({
    localeText,
    communicationPreference: account?.profile?.communicationPreference || 0
  });

  const saveAccount = e => {
    const oldPassword = getInputValueByName(e, 'oldPassword');
    const newPassword = getInputValueByName(e, 'newPassword');

    let accInfo = {
      firstName: getInputValueByName(e, 'firstName'),
      lastName: getInputValueByName(e, 'lastName'),
      email: getInputValueByName(e, 'email'),
      username: getInputValueByName(e, 'email'),
      phone: getInputValueByName(e, 'phone'),
      ...(newPassword && oldPassword && {newPassword, oldPassword}),
      communicationPreference: emailOrMail
    };

    return updateAccount(accInfo);
  };

  const saveAddress = e => {
    const addrInfo = {
      userId: account?.profile?.userId,
      addressLine1: getInputValueByName(e, 'addressLine1'),
      addressLine2: getInputValueByName(e, 'addressLine2'),
      city: getInputValueByName(e, 'city'),
      state: getInputValueByName(e, 'state'),
      postalCode: getInputValueByName(e, 'postalCode'),
      country: account?.profile?.address?.country
    };

    return updateAddress(addrInfo);
  };

  const submitHandler = async e => {
    e.preventDefault();
    setErrors({});

    // Password Change Check - START
    const oldPassword = document.getElementById('input-old-password');
    const newPassword = document.getElementById('input-new-password');
    const confirmNewPassword = document.getElementById(
      'input-confirm-new-password'
    );

    let passwordErrors = [];

    if (!oldPassword.value && (newPassword.value || confirmNewPassword.value)) {
      passwordErrors.push(
        localeText('rw_msg_old_password_required', 'Old password required')
      );
    } else if (
      oldPassword.value &&
      (newPassword.value || confirmNewPassword.value)
    ) {
      passwordErrors = getPasswordErrors(
        newPassword.value,
        confirmNewPassword.value,
        localeText
      );
    }

    if (passwordErrors.length > 0) {
      setErrors(prev => ({...prev, passwordErrors}));
      return;
    }
    // Password Change Check - END

    const saveInfo = async ({
      saveInfoFunc,
      errorCategoryKeyName,
      defaultErrorResponse
    }) => {
      const saveInfoResponse = await saveInfoFunc(e);
      if (saveInfoResponse?.errors) {
        const saveInfoErrors = saveInfoResponse.errors.map(
          error => localeText(error?.error?.slug),
          defaultErrorResponse
        );
        setErrors(prev => ({
          ...prev,
          [errorCategoryKeyName]: saveInfoErrors
        }));
        setSnackBarMessage(saveInfoErrors[0]);
        return false;
      }
      return true;
    };

    // if no Front End errors detected, continue
    const saveAccountSuccess = await saveInfo({
      saveInfoFunc: saveAccount,
      errorCategoryKeyName: 'saveAccountErrors',
      defaultErrorResponse: 'Save Account Error'
    });
    if (!saveAccountSuccess) return;

    // if no errors while saving Account, continue
    oldPassword.value = '';
    newPassword.value = '';
    confirmNewPassword.value = '';

    const saveAddressSuccess = await saveInfo({
      saveInfoFunc: saveAddress,
      errorCategoryKeyName: 'saveAddressErrors',
      defaultErrorResponse: 'Save Address Error'
    });
    if (!saveAddressSuccess) return;

    // if no errors while saveing Address, continue
    setSnackBarMessage(
      localeText(
        'rw_msg_acc_update_successful',
        'Account Successfully Updated!'
      )
    );
  };

  return (
    <HeadingAndExpandableBody
      headingAndExpandableBodyId='my-account'
      heading={localeText('rw_header_my_profile', 'My Profile')}
      expanded={props.expanded}
    >
      {account && (
        <div>
          <form onSubmit={submitHandler}>
            {shouldShowField(regFields.name) && (
              <div className='flex sm:gap-4 flex-col sm:flex-row'>
                <LabelAndInput
                  name='firstName'
                  label={localeText('rw_title_first_name', 'First Name')}
                  type='text'
                  defaultValue={account?.profile?.firstName}
                  className='grow'
                  input_testid='myaccount_input_first_name'
                  label_testid='myaccount_label_firstName'
                />
                <LabelAndInput
                  name='lastName'
                  label={localeText('rw_title_last_name', 'Last Name')}
                  type='text'
                  defaultValue={account?.profile?.lastName}
                  className='grow'
                  input_testid='myaccount_input_last_name'
                  label_testid='myaccount_label_lastName'
                />
              </div>
            )}
            <div className='flex sm:gap-4 flex-col sm:flex-row'>
              {shouldShowField(regFields.mobile) && (
                <LabelAndInput
                  name='phone'
                  label={localeText('rw_title_mobile', 'Mobile')}
                  placeholder={localeText(
                    'rw_title_mobile_placeholder',
                    '10-Digit Number'
                  )}
                  type='text'
                  defaultValue={account?.profile?.phone}
                  className='grow'
                  input_testid='myaccount_input_phone'
                  label_testid='myaccount_label_phone'
                />
              )}
              <LabelAndInput
                name='email'
                label={localeText('rw_title_email', 'Email')}
                type='email'
                defaultValue={account?.profile?.email}
                className='grow'
                required
                input_testid='myaccount_input_email'
                label_testid='myaccount_label_email'
              />
            </div>
            {shouldShowField(regFields.address) && (
              <div className='mt-6 flex sm:gap-4 flex-col sm:flex-row'>
                <LabelAndInput
                  name='addressLine1'
                  label={localeText(
                    'rw_register_title_address_line_1',
                    'Address Line 1'
                  )}
                  placeholder={localeText(
                    'rw_register_title_address_1_placeholder',
                    'Address Line 1'
                  )}
                  type='text'
                  defaultValue={account?.profile?.address?.thoroughfare}
                  className='grow'
                  input_testid='myaccount_input_address_line_1'
                  label_testid='myaccount_label_addressLine1'
                />
                <LabelAndInput
                  name='addressLine2'
                  label={localeText(
                    'rw_register_title_address_line_2',
                    'Address Line 2'
                  )}
                  type='text'
                  placeholder={localeText(
                    'rw_register_title_address_2_placeholder',
                    'Address Line 2'
                  )}
                  defaultValue={account?.profile?.address?.premise}
                  className='grow'
                  input_testid='myaccount_input_address_line_2'
                  label_testid='myaccount_label_addressLine2'
                />
              </div>
            )}
            {(shouldShowField(regFields.city) ||
              shouldShowField(regFields.state)) && (
              <div className='flex sm:gap-4 flex-col sm:flex-row'>
                {shouldShowField(regFields.city) && (
                  <LabelAndInput
                    name='city'
                    label={localeText('rw_register_title_city', 'City')}
                    type='text'
                    placeholder={localeText(
                      'rw_register_title_city_placeholder',
                      'City'
                    )}
                    defaultValue={account?.profile?.address?.locality}
                    className='grow'
                    input_testid='myaccount_input_city'
                    label_testid='myaccount_label_city'
                  />
                )}
                {shouldShowField(regFields.state) && (
                  <LabelAndInput
                    name='state'
                    label={localeText('rw_register_title_state', 'State')}
                    type='text'
                    placeholder={localeText(
                      'rw_register_title_state_placeholder',
                      'State'
                    )}
                    defaultValue={account?.profile?.address?.adminArea}
                    className='grow'
                    input_testid='myaccount_input_state'
                    label_testid='myaccount_label_state'
                  />
                )}
              </div>
            )}
            {shouldShowField(regFields.zipCode) && (
              <div className='flex sm:gap-4 flex-col sm:flex-row'>
                <LabelAndInput
                  name='postalCode'
                  label={localeText('rw_register_title_zip_code', 'Zip Code')}
                  placeholder={localeText(
                    'rw_register_title_zip_code_placeholder',
                    'Zip Code'
                  )}
                  defaultValue={account?.profile?.address?.postalCode}
                  className='grow'
                  input_testid='myaccount_input_postal_code'
                  label_testid='myaccount_label_postalCode'
                />
                <LabelAndInput
                  label='filler'
                  type='text'
                  className='grow sm:invisible sm:inline-block hidden'
                />
              </div>
            )}
            <LabelAndInput
              name='oldPassword'
              label={localeText('rw_title_old_password', 'Old Password')}
              placeholder={localeText(
                'rw_title_old_password_placeholder',
                'Old Pasword'
              )}
              type='password'
              className='mt-8 grow'
              id='old-password'
              inputId='old-password'
              autoComplete='off'
              input_testid='myaccount_input_old_password'
              label_testid='myaccount_label_oldPassword'
            />
            <div className='flex sm:gap-4 flex-col sm:flex-row'>
              <LabelAndInput
                name='newPassword'
                label={localeText('rw_title_new_password', 'New Password')}
                placeholder={localeText(
                  'rw_title_new_password_placeholder',
                  'New Password'
                )}
                type='password'
                className='grow'
                id='new-password'
                inputId='new-password'
                autoComplete='off'
                input_testid='myaccount_input_new_password'
                label_testid='myaccount_label_newPassword'
              />
              <LabelAndInput
                name='confirmNewPassword'
                label={localeText(
                  'rw_title_new_password_confirm',
                  'Confirm New Password'
                )}
                placeholder={localeText(
                  'rw_title_new_password_confirm_placeholder',
                  'Retype New Password'
                )}
                type='password'
                className='grow'
                id='confirm-new-password'
                inputId='confirm-new-password'
                autoComplete='off'
                input_testid='myaccount_input_confirm_new_password'
                label_testid='myaccount_label_confirmNewPassword'
              />
            </div>
            {
              // consolidate all errors in all categories into 1 array
              errors &&
                [].concat
                  .apply([], Object.values(errors))
                  //then display them all
                  .map((error, index) => (
                    <ErrorNote key={index}>{error}</ErrorNote>
                  ))
            }
            <AuthComProm />
            <Button
              className='mt-8'
              type='submit'
              loading={loading}
              data-testid='myaccount_submit_button_save_account'
            >
              {localeText('rw_button_save_account', 'Save Profile')}
            </Button>
          </form>
        </div>
      )}
    </HeadingAndExpandableBody>
  );
}

export default MyAccount;
